// 体験談詳細ページの章アンカースクロール共通ユーティリティ (#10592)
//
// 施設詳細ページの体験談カセット「続きをみる」（hash付きリンク）から
// 遷移してきた際に、一度ページトップを見せてから該当章へスムーズスクロールする UX を提供する。
//
// 設計方針:
// - カセット側クリックハンドラ（reviews_and_experiences/*/component_controller.js）が
//   クリックを横取りし、hash を sessionStorage に保存して hash なしURLへ遷移する。
//   これによりブラウザのネイティブhashジャンプが発生せず、「対象章が一瞬見える」UX問題を回避。
// - このファイル（遷移先側）が sessionStorage から target を読み取ってスクロールを実行する。
// - 全リソースのロード完了(window.load)を待ってスクロールすることで、
//   画像遅延ロードによるレイアウトシフトでtarget位置がズレる問題を防ぐ。
// - smooth-scroll ライブラリを優先使用、失敗時はネイティブ scrollIntoView にフォールバック。
// - bfcache 復元時は pageshow(persisted=true) で再実行。
//
// 直リンク/ブックマーク（カセット経由でなくhash付きURLを直接開いた場合）は、
// sessionStorage が空なのでこのスクリプトは何もしない。
// ブラウザのネイティブhashジャンプで target に着地する（スムーズスクロール演出なし）。

// 体験談詳細ページ側 section_dom_id のプレフィックス。
//
// ⚠️ 同期必須: 以下4箇所すべてで同じプレフィックス文字列をハードコードしている。
// 片方だけ変更するとカセットのリンク href と遷移先 DOM ID が乖離し、
// 「リンクは生成されるがスクロールが silent に失敗する」状態になる。
//   - この JS 定数（`SECTION_HASH_PREFIXES`）
//   - Site::Pages::Facility::MoveInExperiences::Show::Common::Component#section_dom_id
//   - Site::Pages::Facility::MoveOutExperiences::Show::Common::Component#section_dom_id
//   - Site::Parts::Facility::ReviewsAndExperiences::Common::Component::SECTION_DOM_ID_PREFIX
//
// 他セクション（ヘッダ見出しや CTA など hash 付きだが章ではないもの）を
// 誤って拾わないため、章アンカーのみをプレフィックスマッチで許可する。
const SECTION_HASH_PREFIXES = [
  '#move-in-experience-section-',
  '#move-out-experience-section-',
]

// カセット側クリックハンドラが設定する sessionStorage キー。
// 値形式: { hash: string, timestamp: number } の JSON 文字列。
// カセット側（reviews_and_experiences/*/component_controller.js の handleClick）と同期する。
const INTENT_STORAGE_KEY = 'experiencesAnchorIntent'

// sessionStorage に保存された intent の有効期限。
// 古い intent が別ページで誤って消費されるのを防ぐための上限。
// （通常、クリック直後の遷移では数秒以内に消費される）
const INTENT_TTL_MS = 10_000

// トップ表示後、該当章へスクロール開始するまでの待機時間。
// ユーザーに「一度冒頭を視認させる」UXのための意図的な停止時間。
// 300ms は体感上トップを認識できる最小時間として選定（類似実装 summary/hash_scroll.js と同値）。
const SCROLL_START_DELAY_MS = 300

// target 要素が見つからない場合のリトライ上限。
// window.load 後も描画が遅延するケース（Turbo 等の遅延描画）の保険。
// 通常は初回で見つかるため、このリトライが発火することはほぼない。
const TARGET_LOOKUP_MAX_ATTEMPTS = 30 // 60Hz環境で約0.5秒（rAF 30フレーム相当）

// window.load が長時間発火しない場合の強制実行までのタイムアウト。
// 画像404多発、極端に重いページ、遅い回線などで load が来ない場合でも
// 最終的にスクロールが走るようにする保険。
// 3000ms は「体感待機上限」と「異常系救済」のバランスで選定。
// 通常ページでは load が 1〜2 秒以内に発火するため、この保険は発動しない。
const LOAD_FALLBACK_TIMEOUT_MS = 3000

// 体験談詳細ページの Stimulus コントローラの connect() で呼び出す。
// 呼び出し側は initialize() で this.scroll = new SmoothScroll(...) を設定済みであること
// （未設定でも scrollIntoView にフォールバックするため致命的ではない）。
export function setupAnchorScroll (controller) {
  scrollFromIntent(controller)

  // bfcache復元時のみ再実行（persisted=true）。
  // persisted=false の pageshow は通常ロードと同タイミングで、
  // 上記の setupAnchorScroll からの初回呼び出しと二重実行になるため除外する。
  controller.boundPageShow = (event) => {
    if (event.persisted) scrollFromIntent(controller)
  }
  window.addEventListener('pageshow', controller.boundPageShow)
}

// 体験談詳細ページの Stimulus コントローラの disconnect() で呼び出す
export function teardownAnchorScroll (controller) {
  if (controller.boundPageShow) {
    window.removeEventListener('pageshow', controller.boundPageShow)
  }
}

function scrollFromIntent (controller) {
  let intent
  try {
    const raw = sessionStorage.getItem(INTENT_STORAGE_KEY)
    if (!raw) return
    // 一度だけ消費する（重複実行防止）。target未発見時もstorageから除去する方が
    // 副作用が少ない（残留intentが別ページで誤発火するリスクを抑える）。
    sessionStorage.removeItem(INTENT_STORAGE_KEY)
    intent = JSON.parse(raw)
  } catch {
    // sessionStorage 無効 or 破損 JSON の場合は silent fail
    // （カセット経由のスクロール演出は progressive enhancement として諦め、
    //   ブラウザのネイティブ動作に委ねる）
    return
  }

  if (!intent || typeof intent.hash !== 'string' || typeof intent.timestamp !== 'number') return
  // TTL超過のintentは古いクリックの残留とみなして破棄
  if (Date.now() - intent.timestamp > INTENT_TTL_MS) return

  const hash = intent.hash
  // 章アンカー以外（想定外のキー）は処理しない
  if (!SECTION_HASH_PREFIXES.some(prefix => hash.startsWith(prefix))) return

  // 要件「一度ページトップを見せる」を満たすため、即時にトップへ戻す。
  // カセット経由では hash なし URL で遷移しているため、ブラウザのネイティブhashジャンプは
  // 元々発生しないが、bfcache 復元時にページが前回のスクロール位置を保持している可能性
  // への対応としても機能する。
  window.scrollTo(0, 0)

  // 全リソース（画像・フォント・CSS）のロード完了を待ってからスクロール開始。
  // 画像遅延ロードによるレイアウトシフトで target 位置がズレるのを防ぐ。
  //
  // 早期 return のパスに入るケース:
  // (a) bfcache 復元時（pageshow persisted=true）
  // (b) Stimulus connect が window.load 後に発火した通常ケース
  // どちらも既にレイアウトは安定しているため待機不要。
  if (document.readyState === 'complete') {
    findElementAndScroll(controller, hash, 0)
    return
  }

  let executed = false
  const execute = () => {
    if (executed) return
    executed = true
    findElementAndScroll(controller, hash, 0)
  }
  window.addEventListener('load', execute, { once: true })
  // 遅い回線や画像404多発などで load が来ない場合の保険
  setTimeout(execute, LOAD_FALLBACK_TIMEOUT_MS)
}

function findElementAndScroll (controller, hash, attempt) {
  const element = document.querySelector(hash)

  if (element) {
    executeScroll(controller, element)
    return
  }

  if (attempt >= TARGET_LOOKUP_MAX_ATTEMPTS) return

  // requestAnimationFrame で次フレームに再試行
  requestAnimationFrame(() => findElementAndScroll(controller, hash, attempt + 1))
}

function executeScroll (controller, element) {
  // スクロール開始直前に再度トップへ戻す。理由は以下の2点：
  // (a) window.load 待機中に画像/フォントの遅延ロードでレイアウトシフトが発生し、
  //     scrollFromIntent で戻したトップ位置から意図せずズレている可能性がある
  // (b) ユーザーが手動でスクロールしていた可能性に備える
  // これにより「冒頭視認→該当章へ」の UX を保証する。
  window.scrollTo(0, 0)

  // 冒頭表示後、該当章へスムーズスクロール
  setTimeout(() => {
    // setTimeout 待機中に DOM が差し替わって element が切り離されている可能性に備える
    // （Turbo navigation、親要素の再描画、Stimulus controller の再接続 等）。
    // 切り離された要素に scrollIntoView/animateScroll すると例外または無効動作になるため、
    // isConnected で事前確認する。
    if (!element.isConnected) return
    smoothScrollTo(controller, element)
  }, SCROLL_START_DELAY_MS)
}

function smoothScrollTo (controller, element) {
  // smooth-scroll ライブラリが利用可能なら優先（cross-browser で安定、offset対応）
  if (controller.scroll && typeof controller.scroll.animateScroll === 'function') {
    try {
      controller.scroll.animateScroll(element)
      return
    } catch {
      // ライブラリで失敗した場合はネイティブにフォールバック
    }
  }

  // フォールバック: ネイティブ scrollIntoView
  element.scrollIntoView({ behavior: 'smooth', block: 'start' })
}
